/**!
 *  Trending content widget.
 *  Author: Bjorn Tollstrom <bjorn@rodolfo.se>
 */

import React from "react";
import Widget from "../../widget.js";
import "./trendingContent.scss";
import API from "Class/API";
import TopListItem from "Components/UI/TopListItem";
import Spinner from "Components/Feedback/Spinner";
import WidgetWrapper from "Components/UI/WidgetWrapper";

class WidgetTrendingContent extends Widget
{
    constructor(props)
    {
        super(props);
        this.Fields = this.SetFields({
            title:
            {
                default: "Trending Content",
                insert: true,
                label: "Title",
                reset: true,
                type: "text",
                translate: true
            },
            community:
            {
                label: "Community",
                placeholder: "Search for community...",
                type: "content",
                types: ["community"]
            },
            communityNotice:
            {
                displayIf: ["community", "==", 0],
                label: "If no community is specified, the trending content for the current community will be loaded.",
                type: "notice"
            },
            limit:
            {
                label: "Max. number of items",
                type: "number",
                default: 16
            },
            days:
            {
                label: "Number of days",
                type: "number",
                default: 7
            }
        }, {
            backgroundColor: "transparent",
            textColor: "black"
        }, true);
        this.Mounted = false;
        this.Name = "Trending Content";
        this.Translate =
        {
            patience: "Compiling... Please have patience.",
            nocontent: "No trending content"
        };
        this.state =
        {
            content: [],
            error: false,
            loading: false,
            translations: []
        };
    }

    /**
     * Load trending content on mount.
     * @return void
     */

    componentDidMount()
    {
        this.Mounted = true;
        this.Reload();
    }

    /**
     * Reload trending content if the community is changed.
     * @return void
     */

    componentDidUpdate(prevProps)
    {
        const {content: ct1} = this.props;
        const {content: ct2} = prevProps;
        const {community: cm1, days: d1, limit: l1} = ct1 || {};
        const {community: cm2, days: d2, limit: l2} = ct2 || {};
        const C1 = (cm1 && cm1.length) ? cm1[0][0] : 0;
        const C2 = (cm2 && cm2.length) ? cm2[0][0] : 0;
        if (C1 !== C2 || d1 !== d2 || l1 !== l2)
        {
            this.Load(C1, d1, l1);
        }
    }

    /**
     * Register unmount.
     * @return void
     */

    componentWillUnmount()
    {
        this.Mounted = false;
    }

    Item = ({id}, index) =>
    {
        const {translate} = this.Content();
        return (
            <TopListItem
                className="WidgetTrendingItem"
                id={id}
                index={index}
                key={id}
                translate={translate}
            />
        );
    }

    /**
     * Load the trending content.
     * @param integer community - Community id.
     * @param integer days - Number of days to compile.
     * @param integer limit - Number of content items to compile.
     * @return void
     */

    Load = (community = 0, days = 7, limit = 10) =>
    {
        const {context, contextId} = this.props;
        const Current = context === "community" ? contextId : 0;
        const Community = community || Current;
        this.setState({
            error: false,
            leaderboard: {},
            loading: true
        });
        API.Request("trending/content",
        {
            community: Community,
            interval: days * 24,
            limit
        }, async (response) =>
        {
            if (!this.Mounted)
            {
                return;
            }
            const {content} = response;
            if (!content)
            {
                this.setState({error: true, loading: false});
            }
            else {
                this.setState({content, loading: false});
            }
        });
    }

    /**
     * Reload trending content.
     * @param integer community - Community id.
     * @return void
     */

    Reload = () =>
    {
        const {content, days, limit} = this.props;
        const {community: c} = content || {};
        const Community = (c && c.length) ? c[0][0] : 0;
        this.Load(Community, days, limit);
    }

    render()
    {
        const WidgetContent = this.Content();
        const {active, hover} = this.props;
        const {content, loading} = this.state;
        const {title} = WidgetContent;
        const CA = this.ClassNames(["WidgetTrending"]);
        if (active)
        {
            CA.push("Active");
        }
        if (hover)
        {
            CA.push("Hover"); 
        }
        const Content = [];
        const Items = [];
        if (content && content.length)
        {
            content.forEach((c, i) =>
            {
                Items.push(this.Item(c, i));
            });
        }
        if (!Items.length && loading)
        {
            Content.push(<Spinner
                className="WidgetSpinner"
                key="spinner"
                message={this.GetTranslation("patience")}
                overlay={true}
            />);
        }
        else if (!Items.length)
        {
            Content.push(<div
                className="WidgetEmpty"
                key="empty"
            >{this.GetTranslation("nocontent")}</div>);
        }
        else
        {
            Content.push(<div
                className="WidgetTrendingContentItems"
                key="items"
            >{Items}</div>);
        }
        return (
            <div className={CA.join(" ")} ref={widget => this.RefWidget = widget} style={this.Style()}>
                {this.Toolbar()}
                <WidgetWrapper
                    {...WidgetContent}
                    className="WidgetTrendingContentWrapper"
                    title={title}
                >
                    {Content}
                </WidgetWrapper>
            </div>
        );
    }
}

export default WidgetTrendingContent;